import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "@rotorstudios/fe-modules";
import { useCmsContext } from "@rotorstudios/fe-cms";

import { useApiService } from "./useApiService";
import {
  iCookieData,
  iDealer,
  iDealership,
  iInitConfig,
  iPinData,
  iUser,
  iVehicleData,
} from "./types";
import {
  USER_COOKIE_LOCAL_DATA_ID,
  USER_COOKIE_EXTERNAL_DATA_ID,
  SUBDOMAIN_QA,
} from "./static";
import { useCookie } from "../../hooks/useCookie";
import { getConfigFromMaterialCode } from "../../helpers/cmeHelper";
import { extractDealerSubdomainFromURL } from "../../helpers/utils";

type iLoadingData = {
  isInitialDataValidated?: boolean;
  urlParams?: any;
  pinData?: iPinData;
  vehicleData?: iVehicleData;
  dealershipData?: iDealership;
  dealerData?: iDealer;
  userData?: iUser;
  updatePinData: (state: iPinData) => void;
  updateUserName: (name?: string) => void;
  updateVehicle: (vehicleId: string) => void;
};

const useAppLoadingData = (): iLoadingData => {
  const { apiGetUserConfiguration, apiGetDealersByURLSubdomain, apiGetDealershipInfo } =
    useApiService();
  const { fixVersionManifest, getSiloFromVehicleId, setSelectedSiloId } = useCmsContext();
  const { search } = useLocation();

  const [isInitialDataValidated, setIsInitialDataValidated] = useState<boolean>(false);

  const [pinData, setPinData] = useState<iPinData>({ pin: "", isValid: false });
  const [vehicleData, setVehicleData] = useState<iVehicleData>();
  const [dealershipData, setDealershipData] = useState<iDealership>();
  const [dealerData, setDealerData] = useState<iDealer>();
  const [userData, setUserData] = useState<iUser>({ name: "", skipInput: false });
  const [urlParams, setUrlParams] = useState(
    Object.fromEntries(new URLSearchParams(search)),
  );

  // ********************
  // App initialisation data (url and cookies)
  // ********************
  const [localCookieData] = useCookie<iCookieData>(
    USER_COOKIE_LOCAL_DATA_ID,
    {} as iCookieData,
  );
  const [externalCookieData] = useCookie<iCookieData>(
    USER_COOKIE_EXTERNAL_DATA_ID,
    {} as iCookieData,
  );

  // ********************
  // Data helpers
  // ********************
  const getInitConfigFromPin = useCallback(
    (config: any): iInitConfig => ({
      mvob: config.mvob,
      colour: config.colour,
      interiorTrim: config.interiorTrim,
      accessories: config.accessories,
    }),
    [],
  );

  const getInitConfigFromUrl = useCallback(
    (matCode?: string, acc?: string): iInitConfig | undefined => {
      const baseConfig = matCode ? getConfigFromMaterialCode(matCode) : undefined;
      const accessories = acc?.split(",") ?? undefined;
      return accessories ? { ...baseConfig, accessories } : baseConfig;
    },
    [],
  );

  const getInitConfigFromCookies = useCallback(
    (vehicleId?: string): iInitConfig | undefined => {
      const vehicle =
        externalCookieData?.vehicles?.find((v: any) => v.model === vehicleId) ??
        localCookieData?.vehicles?.find((v: any) => v.model === vehicleId);
      const baseConfig = vehicle?.materialCode
        ? getConfigFromMaterialCode(vehicle.materialCode)
        : undefined;
      const accessories = vehicle?.accessories ?? undefined;
      return accessories ? { ...baseConfig, accessories } : baseConfig;
    },
    [externalCookieData, localCookieData],
  );

  const getUserDataFromCookies = useCallback((): iUser => {
    const user = externalCookieData?.user ?? localCookieData?.user;
    return user ?? { name: "", skipInput: false };
  }, [externalCookieData, localCookieData]);

  // ********************
  // Data setters
  // ********************
  const setVehicleDataFromPin = useCallback(
    (pin: string, siloId: string, config: iInitConfig) => {
      const cmsSiloData = fixVersionManifest.silos?.get(siloId);
      if (cmsSiloData) {
        const initConfig = getInitConfigFromPin(config);
        setVehicleData({
          vehicleId: cmsSiloData.vehicle_id,
          siloId: cmsSiloData.silo_id,
          initConfig,
        });
        setSelectedSiloId(cmsSiloData.silo_id);
        setPinData({ pin, isValid: true });
      }
    },
    [fixVersionManifest, getInitConfigFromPin, setSelectedSiloId],
  );

  const setVehicleDataFromUrlAndCookie = useCallback(
    (vehicleId?: string, matCode?: string, accessories?: string) => {
      const cmsSiloData = vehicleId ? getSiloFromVehicleId(vehicleId) : null;
      if (cmsSiloData) {
        const initConfig = matCode
          ? getInitConfigFromUrl(matCode, accessories)
          : getInitConfigFromCookies(vehicleId);
        setVehicleData({
          vehicleId: cmsSiloData.vehicle_id,
          siloId: cmsSiloData.silo_id,
          initConfig,
        });
        setSelectedSiloId(cmsSiloData.silo_id);
      }
    },
    [
      getSiloFromVehicleId,
      getInitConfigFromUrl,
      getInitConfigFromCookies,
      setSelectedSiloId,
    ],
  );

  const getDealershipData = useCallback(
    async (sapCode?: string) => {
      const subdomain = extractDealerSubdomainFromURL() ?? "";

      if (sapCode) {
        return await apiGetDealershipInfo(sapCode);
      }

      if (subdomain && subdomain !== SUBDOMAIN_QA) {
        const subdomainDealers = await apiGetDealersByURLSubdomain(subdomain);
        return subdomainDealers?.length === 1 ? subdomainDealers[0] : null;
      }

      return null;
    },
    [apiGetDealersByURLSubdomain, apiGetDealershipInfo],
  );

  const handleUpdateSapCodeUrl = useCallback(
    (sapCode: string) => {
      if (!urlParams.pin && !urlParams.SC) {
        const url = new URL(globalThis.window.location.href);
        url.searchParams.set("SC", sapCode);
        globalThis.window.history.replaceState({}, "", url.toString());
      }
    },
    [urlParams.SC, urlParams.pin],
  );

  const setDealershipAndDealerData = useCallback(
    async (sapCode: string, dealer?: iDealer) => {
      const data = await getDealershipData(sapCode);

      if (data) {
        setDealershipData(data);
        setDealerData({
          firstName: dealer?.firstName ?? data.conciergeFirstName,
          lastName: dealer?.lastName ?? data.conciergeLastName,
          email: dealer?.email ?? data.conciergeEmail,
        });
        setTimeout(() => {
          if (data.sapCode) {
            setUrlParams((prev) => ({
              ...prev,
              SC: data.sapCode,
            }));
            handleUpdateSapCodeUrl(data.sapCode);
          }
        }, 1);
      }
    },
    [getDealershipData, handleUpdateSapCodeUrl],
  );

  // ********************
  // Data hierarchy
  // ====================
  // 1. Pin Verification = Url has pin
  // 2. Non-Pin Verification = Url does not have pin
  //    Optional params: vehicle, SC, MC, accessories
  //    If no params, use cookies (local cookie > external cookie)
  // ********************
  const urlPinVerification = useMemo(
    (): boolean =>
      Boolean(urlParams?.pin && fixVersionManifest && !isInitialDataValidated),
    [urlParams?.pin, fixVersionManifest, isInitialDataValidated],
  );
  const urlNonPinVerification = useMemo(
    (): boolean =>
      Boolean(!urlParams?.pin && fixVersionManifest && !isInitialDataValidated),
    [urlParams?.pin, fixVersionManifest, isInitialDataValidated],
  );

  // ********************
  // 1. Pin Verification:
  // ---- Get vehicle, sap code, init config and user data from url pin
  // ---- ex. ?pin=xxxxx
  // ********************
  useEffect(() => {
    const handlePinVerification = async () => {
      const { pin } = urlParams;
      const data = await apiGetUserConfiguration(pin);
      if (data) {
        const customerName = data.firstName;
        const dealerName = data.dealer.firstName;

        setUserData({ name: customerName ?? dealerName, skipInput: true });
        setVehicleDataFromPin(data.pin, data.vehicle, data.config);
        await setDealershipAndDealerData(data.sapCode, data.dealer);
      }
      setIsInitialDataValidated(true);
    };

    if (urlPinVerification) {
      handlePinVerification();
    }
  }, [
    urlPinVerification,
    urlParams,
    apiGetUserConfiguration,
    setVehicleDataFromPin,
    setDealershipAndDealerData,
  ]);

  // ********************
  // 2. Non-Pin Verification:
  // ---- Get vehicle, sap code and init config data from url params
  // ---- OR, get init config, user data from cookies
  // ---- ex. ?vehicle=xxxxx&SC=xxxxx&MC=xxxxx&accessories=xxxxx
  // ********************
  useEffect(() => {
    const handleNonPinVerification = async () => {
      const { vehicle, MC, accessories, SC } = urlParams;
      const user = getUserDataFromCookies();

      setUserData(user);
      setVehicleDataFromUrlAndCookie(vehicle, MC, accessories);
      await setDealershipAndDealerData(SC);

      setIsInitialDataValidated(true);
    };

    if (urlNonPinVerification) {
      handleNonPinVerification();
    }
  }, [
    urlNonPinVerification,
    urlParams,
    setVehicleDataFromUrlAndCookie,
    setDealershipAndDealerData,
    getUserDataFromCookies,
  ]);

  // ********************
  // Update state
  // ********************
  const updatePinData = useCallback((state: iPinData) => {
    setPinData(state);
  }, []);

  const updateVehicle = useCallback(
    (vehicleId: string) => {
      const cmsSiloData = getSiloFromVehicleId(vehicleId);
      if (cmsSiloData) {
        setVehicleData({
          vehicleId: cmsSiloData.vehicle_id,
          siloId: cmsSiloData.silo_id,
          initConfig: getInitConfigFromCookies(vehicleId),
        });
        setSelectedSiloId(cmsSiloData.silo_id);
      }
    },
    [getSiloFromVehicleId, getInitConfigFromCookies, setSelectedSiloId],
  );

  const updateUserName = useCallback((name?: string) => {
    setUserData({ name, skipInput: true });
  }, []);

  return {
    isInitialDataValidated,
    urlParams,
    pinData,
    vehicleData,
    dealershipData,
    dealerData,
    userData,
    //
    updatePinData,
    updateUserName,
    updateVehicle,
  };
};

export default useAppLoadingData;
